import type { BasisComponent, FeeLine, FeeRule, ValueSource } from "@netkeep/fee-types";
import {
  type Dec,
  FeeEngineError,
  HUNDRED,
  ZERO,
  formatMoney,
  formatPercent,
  maxDec,
  minDec,
  moneyString,
  parseAmount,
  roundMoney,
} from "./money";

export type BasisAmounts = Record<BasisComponent, Dec>;

const BASIS_LABELS: Record<BasisComponent, string> = {
  ITEM_PRICE: "item",
  BUYER_SHIPPING: "shipping",
  SALES_TAX: "tax",
};

export function describeBasis(basis: readonly BasisComponent[]): string {
  return basis.map((b) => BASIS_LABELS[b]).join(" + ");
}

/** Sums the selected components, ignoring duplicates in the basis list. */
export function basisAmount(basis: readonly BasisComponent[], amounts: BasisAmounts): Dec {
  return [...new Set(basis)].reduce((sum, component) => sum.plus(amounts[component]), ZERO);
}

/** Throws if a rule is structurally invalid. */
export function assertValidRule(rule: FeeRule): void {
  const where = `rule ${rule.code}`;
  if (rule.percent != null && rule.percentTiers != null) {
    throw new FeeEngineError(`${where}: percent and percentTiers are mutually exclusive`, rule.code);
  }
  if (rule.fixedAmount != null && rule.fixedTiers != null) {
    throw new FeeEngineError(`${where}: fixedAmount and fixedTiers are mutually exclusive`, rule.code);
  }
  if (rule.basis.length === 0) {
    throw new FeeEngineError(`${where}: basis must include at least one component`, rule.code);
  }
  for (const tiers of [rule.percentTiers, rule.fixedTiers]) {
    if (!tiers) continue;
    if (tiers.length === 0) throw new FeeEngineError(`${where}: tier list is empty`, rule.code);
    let previous: Dec | null = null;
    tiers.forEach((tier, i) => {
      const isLast = i === tiers.length - 1;
      if (tier.upTo == null) {
        if (!isLast) throw new FeeEngineError(`${where}: only the last tier may be unbounded`, rule.code);
        return;
      }
      if (isLast) throw new FeeEngineError(`${where}: the last tier must be unbounded (upTo: null)`, rule.code);
      const upTo = parseAmount(tier.upTo, `${where} tier upTo`);
      if (previous && !upTo.greaterThan(previous)) {
        throw new FeeEngineError(`${where}: tiers must be strictly ascending`, rule.code);
      }
      previous = upTo;
    });
  }
  if (rule.minimumFee != null && rule.maximumFee != null) {
    const min = parseAmount(rule.minimumFee, `${where} minimumFee`);
    const max = parseAmount(rule.maximumFee, `${where} maximumFee`);
    if (min.greaterThan(max)) throw new FeeEngineError(`${where}: minimumFee exceeds maximumFee`, rule.code);
  }
}

/** Marginal (bracket) percentage: each tier's rate applies only to its slice. */
function tieredPercent(rule: FeeRule, basis: Dec, currency: string): { raw: Dec; text: string } {
  const tiers = rule.percentTiers ?? [];
  let raw = ZERO;
  let lower = ZERO;
  const parts: string[] = [];
  for (const tier of tiers) {
    const pct = parseAmount(tier.percent, `rule ${rule.code} tier percent`);
    const upper = tier.upTo == null ? null : parseAmount(tier.upTo, `rule ${rule.code} tier upTo`);
    const top = upper == null ? basis : minDec(basis, upper);
    const slice = top.minus(lower);
    if (slice.greaterThan(0)) {
      raw = raw.plus(slice.times(pct).dividedBy(HUNDRED));
      parts.push(`${formatPercent(pct)} × ${formatMoney(slice, currency)}`);
    }
    if (upper == null || basis.lessThanOrEqualTo(upper)) break;
    lower = upper;
  }
  return { raw, text: parts.length > 0 ? parts.join(" + ") : `0% × ${formatMoney(basis, currency)}` };
}

function selectFixed(rule: FeeRule, basis: Dec): Dec {
  if (rule.fixedTiers) {
    const tier = rule.fixedTiers.find(
      (t) => t.upTo == null || basis.lessThanOrEqualTo(parseAmount(t.upTo, `rule ${rule.code} tier upTo`)),
    );
    return tier ? parseAmount(tier.amount, `rule ${rule.code} tier amount`) : ZERO;
  }
  return parseAmount(rule.fixedAmount, `rule ${rule.code} fixedAmount`);
}

/**
 * Computes one fee line.
 *
 *   basis   = Σ selected components (item price, buyer shipping, sales tax)
 *   percent = basis × rate ÷ 100           (or Σ marginal tier slices)
 *   fixed   = fixedAmount, or the first fixed tier with basis ≤ upTo
 *   amount  = clamp(round(percent) + fixed, minimumFee, maximumFee)
 *
 * No fee is charged on a zero basis (no transaction took place).
 * Minimums apply only when the basis is positive.
 */
export function computeFeeLine(
  rule: FeeRule,
  amounts: BasisAmounts,
  currency: string,
  source: ValueSource,
): FeeLine {
  assertValidRule(rule);
  const basis = basisAmount(rule.basis, amounts);
  const basisText = `${formatMoney(basis, currency)} (${describeBasis(rule.basis)})`;

  if (basis.isZero()) {
    return {
      code: rule.code,
      label: rule.label,
      category: rule.category,
      source,
      basis: [...rule.basis],
      basisAmount: moneyString(basis, currency),
      percentPortion: moneyString(ZERO, currency),
      fixedPortion: moneyString(ZERO, currency),
      amount: moneyString(ZERO, currency),
      explanation: `No fee: basis ${basisText} is zero`,
    };
  }

  let percentRaw = ZERO;
  let percentText: string | null = null;
  if (rule.percentTiers) {
    const tiered = tieredPercent(rule, basis, currency);
    percentRaw = tiered.raw;
    percentText = tiered.text;
  } else if (rule.percent != null) {
    const pct = parseAmount(rule.percent, `rule ${rule.code} percent`);
    percentRaw = basis.times(pct).dividedBy(HUNDRED);
    percentText = `${formatPercent(pct)} × ${basisText}`;
  }

  const percentPortion = roundMoney(percentRaw, currency);
  const fixedPortion = roundMoney(selectFixed(rule, basis), currency);
  let amount = percentPortion.plus(fixedPortion);

  const explanationParts: string[] = [];
  if (percentText) explanationParts.push(percentText);
  if (!fixedPortion.isZero() || !percentText) explanationParts.push(formatMoney(fixedPortion, currency));
  let explanation = explanationParts.join(" + ");
  if (rule.percentTiers) explanation = `${explanation} · tiered on ${describeBasis(rule.basis)}`;

  if (rule.minimumFee != null) {
    const min = parseAmount(rule.minimumFee, `rule ${rule.code} minimumFee`);
    if (amount.lessThan(min)) {
      amount = min;
      explanation += ` → minimum ${formatMoney(min, currency)} applied`;
    }
  }
  if (rule.maximumFee != null) {
    const max = parseAmount(rule.maximumFee, `rule ${rule.code} maximumFee`);
    if (amount.greaterThan(max)) {
      amount = max;
      explanation += ` → capped at ${formatMoney(max, currency)}`;
    }
  }

  return {
    code: rule.code,
    label: rule.label,
    category: rule.category,
    source,
    basis: [...rule.basis],
    basisAmount: moneyString(basis, currency),
    percentPortion: moneyString(percentPortion, currency),
    fixedPortion: moneyString(fixedPortion, currency),
    amount: moneyString(maxDec(amount, ZERO), currency),
    explanation,
  };
}

